import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Typography, Alert, App, Space, Checkbox } from 'antd';
import {
  TeamOutlined,
  AppstoreOutlined,
  MinusOutlined,
  PlusOutlined,
  ArrowLeftOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { Order, OrderItem } from '../../types/orders';
import { SplitConfiguration } from '../../types/splits';
import {
  useCreateOrderSplitsMutation,
  useValidateOrderSplitsMutation,
} from '../../services/splitsApi';
import { useGetOrderDiscountsQuery } from '../../services/ordersApi';
import { itemDiscountsById, netItemTotal } from '../../utils/discounts';
import SeatPill from '../common/SeatPill';
import { allocateBySeatGroups, formatShare, isWholeShare, maxSeatIn, splitEvenly } from '../../utils/seats';
import './splits.css';

const { Text, Title } = Typography;

const MAX_CUENTAS = 10;
const money = (n: number) => `$${n.toFixed(2)}`;

interface SplitCreatorProps {
  visible: boolean;
  orderId: string;
  orderData: Order;
  onComplete: () => void;
  onCancel: () => void;
}

type Mode = 'choose' | 'seats' | 'assign';

/** itemId → cuentaIndex → unidades (pueden ser fracciones: 0.3333) */
type Allocations = Record<string, Record<number, number>>;

const cuentaName = (index: number) => `Cuenta ${index + 1}`;

/**
 * Cómo dividir la cuenta. Tres caminos, uno solo toca la pantalla de asignar:
 *
 * - Por comensal: la orden ya viene etiquetada (C1, C2…) desde la captura. Cada
 *   comensal es una cuenta y lo compartido se reparte entre todos. Un toque.
 * - Partes iguales: N cuentas idénticas. Cada unidad se parte en fracciones
 *   (1/3 de pizza); el backend prorratea el importe y la suma cuadra al centavo.
 * - Por productos: asignador por toque. Se elige la cuenta activa y se tocan
 *   los productos; lo que quede pendiente se puede repartir entre todas.
 *
 * Antes era un wizard de tres pasos con una matriz producto × cuenta de
 * steppers; en tablet no cabía con tres cuentas y el reparto equitativo estaba
 * escondido en una esquina.
 */
const SplitCreator: React.FC<SplitCreatorProps> = ({
  visible,
  orderId,
  orderData,
  onComplete,
  onCancel,
}) => {
  const { message } = App.useApp();
  const [validateOrderSplits, { isLoading: validating }] = useValidateOrderSplitsMutation();
  const [createOrderSplits, { isLoading: creating }] = useCreateOrderSplitsMutation();

  // Cortesías y descuentos de producto viajan con su renglón: el backend prorratea
  // el importe YA descontado, así que la vista previa de cada cuenta también.
  const { data: orderDiscounts } = useGetOrderDiscountsQuery(orderId, { skip: !visible || !orderId });
  const discountsByItem = useMemo(() => itemDiscountsById(orderDiscounts), [orderDiscounts]);
  const netOf = (item: OrderItem) => netItemTotal(item, discountsByItem);

  const items = useMemo<OrderItem[]>(
    () => (orderData.orderItems || []).filter(i => !i.isCancelled),
    [orderData.orderItems]
  );
  const orderTotal = items.reduce((sum, i) => sum + netOf(i), 0);

  // Comensales: los capturados al abrir la mesa y los usados en renglones
  const guestCount = (orderData.adults ?? 0) + (orderData.children ?? 0);
  const maxSeat = maxSeatIn(items);
  const hasSeats = maxSeat > 0;
  const seatCount = Math.max(guestCount, maxSeat);

  const [mode, setMode] = useState<Mode>('choose');
  const [equalParts, setEqualParts] = useState(2);
  const [cuentaCount, setCuentaCount] = useState(2);
  const [activeCuenta, setActiveCuenta] = useState(0);
  const [allocations, setAllocations] = useState<Allocations>({});
  const [errors, setErrors] = useState<string[]>([]);
  // Comensales agrupados en cuentas: [[1],[2],[3]] = uno por cabeza; [[1,2],[3]]
  // = el niño paga con el papá. Cada grupo termina siendo una cuenta.
  const [seatGroups, setSeatGroups] = useState<number[][]>([]);
  const [pickedSeats, setPickedSeats] = useState<number[]>([]);

  useEffect(() => {
    if (!visible) return;
    setMode('choose');
    setEqualParts(Math.min(MAX_CUENTAS, Math.max(2, seatCount)));
    setCuentaCount(Math.min(MAX_CUENTAS, Math.max(2, hasSeats ? seatCount : 2)));
    setActiveCuenta(0);
    setAllocations({});
    setErrors([]);
    setSeatGroups(Array.from({ length: seatCount }, (_, i) => [i + 1]));
    setPickedSeats([]);
    // Solo al abrir: si la orden se refresca a medio flujo (otro dispositivo
    // agregó un producto) no se pierde lo ya asignado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // ---------- helpers de asignación ----------

  const allocatedOf = (item: OrderItem) =>
    Object.values(allocations[item.id] || {}).reduce((s, q) => s + q, 0);
  const pendingOf = (item: OrderItem) => Math.max(0, item.quantity - allocatedOf(item));
  const totalPending = items.reduce((s, i) => s + pendingOf(i), 0);

  const cuentaTotal = (index: number) =>
    items.reduce((sum, item) => {
      const share = allocations[item.id]?.[index] || 0;
      return sum + (share > 0 ? (netOf(item) * share) / item.quantity : 0);
    }, 0);

  const assignOne = (item: OrderItem, cuenta: number, units = 1) => {
    const pending = pendingOf(item);
    if (pending <= 0.0005) return;
    const add = Math.min(units, pending);
    setAllocations(prev => ({
      ...prev,
      [item.id]: { ...(prev[item.id] || {}), [cuenta]: (prev[item.id]?.[cuenta] || 0) + add },
    }));
  };

  const unassignOne = (item: OrderItem, cuenta: number) => {
    setAllocations(prev => {
      const current = prev[item.id]?.[cuenta] || 0;
      if (current <= 0) return prev;
      // Una fracción se quita completa; una unidad entera, de una en una
      const next = isWholeShare(current) ? current - 1 : 0;
      const row = { ...(prev[item.id] || {}) };
      if (next <= 0.0005) delete row[cuenta];
      else row[cuenta] = next;
      return { ...prev, [item.id]: row };
    });
  };

  /** Lo que falte de cada producto se reparte en partes iguales entre todas las cuentas. */
  const splitPendingEvenly = () => {
    setAllocations(prev => {
      const next: Allocations = { ...prev };
      for (const item of items) {
        const allocated = Object.values(next[item.id] || {}).reduce((s, q) => s + q, 0);
        const pending = item.quantity - allocated;
        if (pending <= 0.0005) continue;
        const shares = splitEvenly(pending, cuentaCount);
        const row = { ...(next[item.id] || {}) };
        shares.forEach((share, idx) => {
          if (share > 0) row[idx] = (row[idx] || 0) + share;
        });
        next[item.id] = row;
      }
      return next;
    });
  };

  const changeCuentaCount = (delta: number) => {
    const next = Math.min(MAX_CUENTAS, Math.max(2, cuentaCount + delta));
    if (next === cuentaCount) return;
    if (delta < 0) {
      // Al quitar la última cuenta, lo que tenía vuelve a pendiente
      setAllocations(prev => {
        const cleaned: Allocations = {};
        for (const [itemId, row] of Object.entries(prev)) {
          const copy = { ...row };
          delete copy[next];
          cleaned[itemId] = copy;
        }
        return cleaned;
      });
      if (activeCuenta >= next) setActiveCuenta(next - 1);
    }
    setCuentaCount(next);
  };

  // ---------- construcción de configuraciones ----------

  const configsFromAllocations = (count: number, alloc: Allocations, names?: string[]): SplitConfiguration[] =>
    Array.from({ length: count }, (_, index) => ({
      name: names?.[index] ?? cuentaName(index),
      itemAllocations: items
        .filter(item => (alloc[item.id]?.[index] || 0) > 0)
        .map(item => ({ orderItemId: item.id, allocatedQuantity: alloc[item.id][index] })),
    })).filter(c => c.itemAllocations.length > 0);

  const equalConfigs = (parts: number): SplitConfiguration[] => {
    const alloc: Allocations = {};
    for (const item of items) {
      alloc[item.id] = {};
      splitEvenly(item.quantity, parts).forEach((share, idx) => {
        if (share > 0) alloc[item.id][idx] = share;
      });
    }
    return configsFromAllocations(parts, alloc);
  };

  /**
   * Una cuenta por grupo de comensales. Lo que nadie pidió en particular
   * (compartido) se reparte POR PERSONA, no por cuenta: si el papá paga por él
   * y por el niño, le tocan dos partes de la botella, no una.
   */
  const seatConfigsFor = (groups: number[][]): SplitConfiguration[] =>
    configsFromAllocations(groups.length, allocateBySeatGroups(items, groups));

  /** Lo que le toca a cada grupo, para verlo antes de crear las cuentas. */
  const groupAmount = (group: number[]) => {
    const totalPeople = seatGroups.reduce((sum, g) => sum + g.length, 0) || seatCount;
    const own = items
      .filter(i => i.seatNumber && group.includes(i.seatNumber))
      .reduce((sum, i) => sum + netOf(i), 0);
    const sharedTotal = items.filter(i => !i.seatNumber).reduce((sum, i) => sum + netOf(i), 0);
    return own + (sharedTotal * group.length) / totalPeople;
  };

  const joinPickedSeats = () => {
    if (pickedSeats.length < 2) return;
    setSeatGroups(prev => {
      const merged = prev.flat().filter(s => pickedSeats.includes(s)).sort((a, b) => a - b);
      const rest = prev
        .map(g => g.filter(s => !pickedSeats.includes(s)))
        .filter(g => g.length > 0);
      // La cuenta juntada toma el lugar del comensal más bajo que la compone
      return [...rest, merged].sort((a, b) => a[0] - b[0]);
    });
    setPickedSeats([]);
  };

  const separateGroup = (group: number[]) => {
    setSeatGroups(prev =>
      prev
        .flatMap(g => (g === group ? g.map(s => [s]) : [g]))
        .sort((a, b) => a[0] - b[0])
    );
    setPickedSeats([]);
  };

  // ---------- crear ----------

  const submit = async (splits: SplitConfiguration[]) => {
    setErrors([]);
    if (splits.length < 2) {
      setErrors(['Se necesitan al menos dos cuentas con productos']);
      return;
    }
    try {
      const validation = await validateOrderSplits({ orderId, splits }).unwrap();
      if (!validation.isValid) {
        setErrors(validation.validationErrors);
        return;
      }
      const result = await createOrderSplits({ orderId, splits }).unwrap();
      if (!result.success) {
        setErrors(result.validationErrors.length ? result.validationErrors : [result.message]);
        return;
      }
      message.success(result.message);
      onComplete();
    } catch (error: any) {
      const data = error?.data;
      const list: string[] = data?.validationErrors?.length ? data.validationErrors : [data?.message || 'No se pudo dividir la cuenta'];
      setErrors(list);
    }
  };

  const busy = validating || creating;

  // ---------- vistas ----------

  const renderChoose = () => (
    <div className="split-choose">
      {hasSeats && (
        <div className="split-option split-option--primary">
          <div className="split-option__body">
            <div className="split-option__title">
              <TeamOutlined /> Por comensal
            </div>
            <Text type="secondary">
              {seatCount} cuentas, una por comensal. Lo compartido se reparte entre todos.
            </Text>
            <div className="split-option__preview">
              {Array.from({ length: seatCount }, (_, i) => [i + 1]).map(g => (
                <span key={g[0]} className="split-preview-chip">
                  <SeatPill seatNumber={g[0]} size="small" /> {money(groupAmount(g))}
                </span>
              ))}
            </div>
          </div>
          <Space direction="vertical" align="end" size={4}>
            <Button
              type="primary"
              size="large"
              loading={busy}
              onClick={() => submit(seatConfigsFor(Array.from({ length: seatCount }, (_, i) => [i + 1])))}
            >
              Crear {seatCount} cuentas
            </Button>
            {seatCount > 1 && (
              <Button type="link" size="small" onClick={() => setMode('seats')}>
                Juntar comensales
              </Button>
            )}
          </Space>
        </div>
      )}

      <div className="split-option">
        <div className="split-option__body">
          <div className="split-option__title">Partes iguales</div>
          <Text type="secondary">Cada quien paga lo mismo, sin importar qué pidió.</Text>
          <div className="split-option__stepper">
            <Button
              shape="circle"
              icon={<MinusOutlined />}
              disabled={equalParts <= 2}
              onClick={() => setEqualParts(n => Math.max(2, n - 1))}
              aria-label="Menos personas"
            />
            <span className="split-option__count">{equalParts}</span>
            <Button
              shape="circle"
              icon={<PlusOutlined />}
              disabled={equalParts >= MAX_CUENTAS}
              onClick={() => setEqualParts(n => Math.min(MAX_CUENTAS, n + 1))}
              aria-label="Más personas"
            />
            <Text className="split-option__per">
              <span className="split-money">{money(orderTotal / equalParts)}</span> por persona
            </Text>
          </div>
        </div>
        <Button size="large" loading={busy} onClick={() => submit(equalConfigs(equalParts))}>
          Dividir en {equalParts}
        </Button>
      </div>

      <div className="split-option">
        <div className="split-option__body">
          <div className="split-option__title">
            <AppstoreOutlined /> Por productos
          </div>
          <Text type="secondary">Elige qué producto va en cada cuenta.</Text>
        </div>
        <Button size="large" onClick={() => setMode('assign')}>
          Asignar productos
        </Button>
      </div>
    </div>
  );

  /**
   * Juntar comensales antes de crear las cuentas: el niño no paga la suya, va
   * con el papá. Mismo gesto que cobrar cuentas juntas (marcar varias y
   * juntar), pero aquí queda decidido ANTES de imprimir la precuenta, que es
   * donde el cliente ve cómo salieron.
   */
  const renderSeats = () => (
    <div className="split-assign">
      <Text type="secondary" className="split-assign__hint">
        Marca los comensales que pagan juntos y tócale a <strong>Juntar</strong>. Cada renglón de
        abajo será una cuenta.
      </Text>

      <div className="split-items">
        {seatGroups.map(group => {
          const key = group.join('-');
          const joined = group.length > 1;
          const picked = group.every(s => pickedSeats.includes(s));
          const groupItems = items.filter(i => i.seatNumber && group.includes(i.seatNumber));
          return (
            <div
              key={key}
              className={`split-item${picked ? ' split-item--picked' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() =>
                setPickedSeats(prev =>
                  picked ? prev.filter(s => !group.includes(s)) : [...prev, ...group]
                )
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setPickedSeats(prev =>
                    picked ? prev.filter(s => !group.includes(s)) : [...prev, ...group]
                  );
                }
              }}
            >
              <div className="split-item__main">
                <div className="split-item__name">
                  <Checkbox checked={picked} style={{ marginRight: 8 }} />
                  {group.map(seat => (
                    <SeatPill key={seat} seatNumber={seat} size="small" style={{ marginRight: 4 }} />
                  ))}
                  {joined && <Text type="secondary" style={{ fontSize: 12 }}>pagan juntos</Text>}
                </div>
                <div className="split-item__meta">
                  {groupItems.length > 0
                    ? groupItems.map(i => `${i.quantity}x ${i.dailyMenuName || i.productName}`).join(', ')
                    : 'sin productos propios'}
                </div>
              </div>
              <div className="split-item__side">
                <span className="split-money">{money(groupAmount(group))}</span>
                {joined && (
                  <Button
                    size="small"
                    type="link"
                    onClick={(e) => { e.stopPropagation(); separateGroup(group); }}
                  >
                    Separar
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="split-assign__footer">
        <Space wrap>
          <Button icon={<ArrowLeftOutlined />} onClick={() => { setMode('choose'); setPickedSeats([]); }}>
            Volver
          </Button>
          <Button disabled={pickedSeats.length < 2} onClick={joinPickedSeats}>
            Juntar {pickedSeats.length >= 2 ? `(${pickedSeats.length})` : ''}
          </Button>
        </Space>
        <Button
          type="primary"
          size="large"
          icon={<CheckOutlined />}
          loading={busy}
          disabled={seatGroups.length < 2}
          onClick={() => submit(seatConfigsFor(seatGroups))}
        >
          Crear {seatGroups.length} cuentas
        </Button>
      </div>
    </div>
  );

  const renderAssign = () => (
    <div className="split-assign">
      {/* Cuentas: la activa recibe lo que se toque */}
      <div className="split-cuentas">
        {Array.from({ length: cuentaCount }, (_, idx) => (
          <button
            key={idx}
            type="button"
            className={`split-cuenta${idx === activeCuenta ? ' split-cuenta--active' : ''}`}
            onClick={() => setActiveCuenta(idx)}
            aria-pressed={idx === activeCuenta}
          >
            <span className="split-cuenta__name">{cuentaName(idx)}</span>
            <span className="split-cuenta__total">{money(cuentaTotal(idx))}</span>
          </button>
        ))}
        <div className="split-cuentas__stepper">
          <Button
            size="small"
            shape="circle"
            icon={<MinusOutlined />}
            disabled={cuentaCount <= 2}
            onClick={() => changeCuentaCount(-1)}
            aria-label="Quitar cuenta"
          />
          <Button
            size="small"
            shape="circle"
            icon={<PlusOutlined />}
            disabled={cuentaCount >= MAX_CUENTAS}
            onClick={() => changeCuentaCount(1)}
            aria-label="Agregar cuenta"
          />
        </div>
      </div>

      <Text type="secondary" className="split-assign__hint">
        Toca un producto para mandarlo a <strong>{cuentaName(activeCuenta)}</strong>. Toca una etiqueta para regresarlo.
      </Text>

      <div className="split-items">
        {items.map(item => {
          const pending = pendingOf(item);
          const row = allocations[item.id] || {};
          const canTake = pending > 0.0005;
          return (
            <div
              key={item.id}
              className={`split-item${canTake ? '' : ' split-item--done'}`}
              onClick={() => canTake && assignOne(item, activeCuenta)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); canTake && assignOne(item, activeCuenta); } }}
            >
              <div className="split-item__main">
                <div className="split-item__name">
                  {item.seatNumber ? <SeatPill seatNumber={item.seatNumber} size="small" style={{ marginRight: 6 }} /> : null}
                  <span className="split-item__qty">{item.quantity}x</span> {item.dailyMenuName || item.productName}
                </div>
                {(item.variants?.length > 0 || item.notes) && (
                  <div className="split-item__meta">
                    {item.variants?.map(v => v.variantName).join(', ')}
                    {item.notes ? `${item.variants?.length ? ' · ' : ''}${item.notes}` : ''}
                  </div>
                )}
                <div className="split-item__chips">
                  {Object.entries(row)
                    .filter(([, q]) => q > 0)
                    .map(([idx, q]) => (
                      <button
                        key={idx}
                        type="button"
                        className="split-chip"
                        onClick={(e) => { e.stopPropagation(); unassignOne(item, Number(idx)); }}
                        title={`Quitar de ${cuentaName(Number(idx))}`}
                      >
                        {cuentaName(Number(idx))} · {formatShare(q)}
                      </button>
                    ))}
                  {canTake && (
                    <span className="split-chip split-chip--pending">
                      {pending === item.quantity ? 'sin asignar' : `faltan ${formatShare(pending)}`}
                    </span>
                  )}
                </div>
              </div>
              <div className="split-item__side">
                <span className="split-money">{money(netOf(item))}</span>
                {canTake && item.quantity - allocatedOf(item) > 1 && (
                  <Button
                    size="small"
                    type="link"
                    onClick={(e) => { e.stopPropagation(); assignOne(item, activeCuenta, pending); }}
                  >
                    Todo a {cuentaName(activeCuenta)}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="split-assign__footer">
        <Space wrap>
          <Button icon={<ArrowLeftOutlined />} onClick={() => setMode('choose')}>
            Volver
          </Button>
          {totalPending > 0.0005 && (
            <Button onClick={splitPendingEvenly}>
              Repartir lo pendiente entre todas
            </Button>
          )}
        </Space>
        <Button
          type="primary"
          size="large"
          icon={<CheckOutlined />}
          loading={busy}
          disabled={totalPending > 0.0005}
          onClick={() => submit(configsFromAllocations(cuentaCount, allocations))}
        >
          Crear cuentas
        </Button>
      </div>
    </div>
  );

  return (
    <Modal
      title={
        <Space direction="vertical" size={0}>
          <Title level={5} style={{ margin: 0 }}>Dividir cuenta</Title>
          <Text type="secondary" style={{ fontSize: 13 }}>
            Total <span className="split-money">{money(orderTotal)}</span> · {items.length} producto{items.length === 1 ? '' : 's'}
          </Text>
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={720}
      destroyOnClose
      className="split-creator"
    >
      {errors.length > 0 && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message={errors.length === 1 ? errors[0] : 'Corrige lo siguiente'}
          description={errors.length > 1 ? (
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {errors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          ) : undefined}
          closable
          onClose={() => setErrors([])}
        />
      )}
      {items.length === 0 ? (
        <Alert type="warning" showIcon message="La orden no tiene productos que dividir" />
      ) : mode === 'choose' ? renderChoose() : mode === 'seats' ? renderSeats() : renderAssign()}
    </Modal>
  );
};

export default SplitCreator;
